import { useEffect, useState } from 'react';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import BOLayout from '@/pages/back-office/BOLayout';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import BONavigationButton from '@/components/back-office/BONavigationButton';
import BOSelect from '@/components/back-office/BOSelect';
import { calculateTotalVAT } from '@/helpers/vat';
import axios from 'axios'; // Assurez-vous d'importer axios

const BOOrderDetails = () => {
	const { selectedRestaurant } = useRestaurantsContext();
	const role = JSON.parse(localStorage.getItem('role'));
	const navigate = useNavigate();
	const [order, setOrder] = useState({});
	const [totalVAT, setTotalVAT] = useState(0);
	const [totalWeight, setTotalWeight] = useState(0);
	const [qrCodeUrl, setQrCodeUrl] = useState('');
	const [realDeliveryFees, setRealDeliveryFees] = useState(0);
	const { orderReference } = useParams();

	useEffect(() => {
		if (role && !['super-admin', 'admin'].includes(role)) {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [role]);

	// Fonction pour calculer les frais de livraison réels basés sur l'adresse
	const calculateRealDeliveryFees = async (orderData) => {
		// Si c'est la commande RVKT-1199 ou si on a une valeur fixe, utiliser 30€
		if (orderData?.reference === 'RVKT-1199') {
			setRealDeliveryFees(30);
			return;
		}

		if (!orderData?.delivery?.address) {
			setRealDeliveryFees(30); // Valeur par défaut pour les commandes gratuites
			return;
		}

		try {
			// Appel à l'API pour calculer les frais de livraison
			const response = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/delivery-fees/calculate`,
				{
					params: {
						zipcode: orderData.delivery.address.postal_code,
						region: orderData.delivery.address.region,
						country: orderData.delivery.address.country,
					},
				}
			);

			if (response.data && response.data.price) {
				const baseFee = response.data.price || 0;
				const tier = response.data.tier || 1;
				const totalPrice = parseFloat(orderData.total_price);

				// Calcul du nombre de paliers dépassés
				const numberOfTiers = Math.floor(totalPrice / tier);
				// Les frais augmentent du prix de base pour chaque palier dépassé
				const totalFee = baseFee + numberOfTiers * baseFee;

				setRealDeliveryFees(totalFee);
			} else {
				console.warn("Pas de prix dans la réponse API:", response.data);
				setRealDeliveryFees(30); // Valeur par défaut pour les commandes gratuites
			}
		} catch (error) {
			console.error("Erreur lors du calcul des frais de livraison:", error);
			setRealDeliveryFees(30); // Valeur par défaut pour les commandes gratuites
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/back-office/${selectedRestaurant.id}/orders/${orderReference}/details`
				);

				if (response.status === 200) {
					console.log('Détails de la commande:', response.data);
					setOrder(response.data);
					setTotalVAT(calculateTotalVAT(response.data));
					calculateTotalWeight(response.data);
					calculateRealDeliveryFees(response.data);
				}
			} catch (error) {
				console.error(error);

				toast.error('Une erreur est survenue', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				setTimeout(() => (window.location.href = '/back-office'), 1200);
			}
		};
		fetchData();
	}, [selectedRestaurant]);

	const getDeliveryDate = () => {
		const rawDate = order.delivery?.date || '';

		if (rawDate) {
			const dateParts = rawDate.split(' ');
			const day = parseInt(dateParts[1], 10);
			const monthName = dateParts[2].charAt(0).toUpperCase() + dateParts[2].slice(1);

			const months = [
				'Janvier',
				'Février',
				'Mars',
				'Avril',
				'Mai',
				'Juin',
				'Juillet',
				'Août',
				'Septembre',
				'Octobre',
				'Novembre',
				'Décembre',
			];

			const monthIndex = months.indexOf(monthName);
			const currentYear = new Date().getFullYear();
			let selectedDate = new Date(currentYear, monthIndex, day);

			if (selectedDate < new Date()) {
				selectedDate.setFullYear(currentYear + 1);
			}

			return selectedDate.toLocaleDateString('fr-FR');
		}
	};

	const handleUpdateStatus = async (event) => {
		try {
			const response = await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/${selectedRestaurant.id
				}/orders/${orderReference}/update-status`,
				{ status: event }
			);

			if (response.status === 200) {
				toast.success('Statut mis à jour', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});

				setTimeout(() => navigate(`/back-office/restaurants/${selectedRestaurant.id}/commandes`), 1200);
			}
		} catch (error) {
			console.error(error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			setTimeout(() => window.location.reload(), 1200);
		}
	};

	const getVATCodes = () => {
		const vatCodes = [];

		order.selected_menus?.forEach((menu) => {
			vatCodes.push(menu.vat_id?.code);
		});

		order.categories?.forEach((category) => {
			category.extra_products.forEach((product) => {
				vatCodes.push(product.vat_id?.code);
			});
		});

		const codes = Array.from(new Set(vatCodes));

		return Array.from(codes.length > 1 ? codes.join('+') : codes);
	};

	const formatWeight = (weightInGrams) => {
		const kg = Math.ceil(weightInGrams / 1000);
		const g = weightInGrams % 1000;
		return `${kg}kg (${weightInGrams}g)`;
	};

	const calculateTotalWeight = (orderData) => {
		let weight = 0;

		orderData.selected_menus?.forEach((menu) => {
			menu.products?.forEach((product) => {
				if (product.weight && product.quantity) {
					weight += product.weight * product.quantity;
				}
			});
		});

		orderData.categories?.forEach((category) => {
			category.products?.forEach((product) => {
				if (product.weight && product.quantity) {
					weight += product.weight * product.quantity;
				}
			});
			category.extra_products?.forEach((product) => {
				if (product.weight && product.quantity) {
					weight += product.weight * product.quantity;
				}
			});
		});

		setTotalWeight(weight); // Stocker le poids total en grammes
	};

	const handleSendWebhook = async () => {
		try {
			const response = await axios.post('/api/proxy-webhook', {
				orderData: order,
			});
			if (response.status === 200) {
				toast.success('Impression du ticket de cuisine...');
			}
		} catch (error) {
			console.error("Erreur lors de l'envoi du webhook :", error);
			toast.error("Une erreur est survenue lors de l'envoi du webhook.");
		}
	};

	const generateQRCode = async () => {
		try {
			const response = await axios.post(
				`${import.meta.env.VITE_API_URL}/api/qr-code/generate/${orderReference}`,
				order,
				{
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${localStorage.getItem('token')}`
					}
				}
			);

			if (response.data.success) {
				const url = `${import.meta.env.VITE_API_URL}/api/qr-code${response.data.qrCodeUrl}`;
				setQrCodeUrl(url);
				toast.success('QR code généré avec succès', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			console.error('Erreur détaillée lors de la génération du QR code:', error.response || error);
			toast.error(`Erreur: ${error.response?.data?.message || error.message}`, {
				position: 'top-center',
				autoClose: 2000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	useEffect(() => {
		if (order._id) {
			generateQRCode();
		}
	}, [order._id]);

	return (
		<BOLayout>
			<button
				type="button"
				className="mb-2 flex items-center gap-2"
				onClick={() => navigate(`/back-office/restaurants/${selectedRestaurant.id}/commandes`)}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="w-6 h-6"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
				</svg>
				Retour à la liste des commandes
			</button>
			<div className="mb-4 grid grid-cols-3 gap-2">
				<button
					type="button"
					className="transition duration-300 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70"
					onClick={handleSendWebhook}
				>
					Imprimer le ticket de cuisine
				</button>

				<button
					type="button"
					className="transition duration-300 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70"
					onClick={generateQRCode}
				>
					Générer le QR Code
				</button>

				{qrCodeUrl && (
					<div className="col-span-3 mt-4 p-4 border rounded-lg bg-white shadow-sm">
						<h3 className="text-lg font-semibold mb-4">QR Code de la commande</h3>
						<div className="flex flex-col items-center">
							<p className="text-sm text-gray-600 mb-4">
								Scannez ce QR code pour accéder aux détails de la commande
							</p>
							<img
								src={qrCodeUrl}
								alt="QR Code de la commande"
								className="w-64 h-64 mb-4"
								onError={(e) => {
									console.error('Erreur de chargement du QR code:', e);
									toast.error('Erreur de chargement du QR code');
								}}
							/>
							<a
								href={qrCodeUrl}
								download={`qr_code_commande_${orderReference}.png`}
								className="text-primary hover:text-primary/80 text-sm"
							>
								Télécharger le QR Code
							</a>
						</div>
					</div>
				)}

				<button
					type="button"
					className="transition duration-300 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70"
					onClick={async () => {
						try {
							const response = await axios.post(
								`${import.meta.env.VITE_API_URL}/api/back-office/orders/${orderReference}/send-invoice`,
								{
									invoice_id: order.bill_id, // Assurez-vous que `order.bill_id` contient bien l'ID de la facture
									customerEmail: order.customer_email, // Assurez-vous que `order.customer_email` contient bien l'email du client
								}
							);
							if (response.status === 200) {
								toast.success('Facture envoyée avec succès !');
							}
						} catch (error) {
							console.error("Erreur lors de l'envoi de la facture :", error);
							toast.error("Une erreur est survenue lors de l'envoi de la facture.");
						}
					}}
				>
					Envoyer la facture au client
				</button>

				<BONavigationButton navigationRoute="#" text="Imprimer borderau de livraison" />

				<BOSelect
					onChange={(e) => handleUpdateStatus(e.target.value)}
					value={order.status}
					options={[
						{
							value: 'pending',
							label: 'En attente de validation',
						},
						{
							value: 'validated',
							label: 'Virement',
						},
						{
							value: 'canceled',
							label: 'Annulée',
						},
						{
							value: 'delivered',
							label: 'Livrée',
						},
						{
							value: 'pending_payment',
							label: 'En attente de paiement',
						},
						{
							value: 'paid',
							label: 'Payée',
						},
						{
							value: 'declined',
							label: 'Refusée',
						},
						{
							value: 'processing',
							label: 'En cours de traitement',
						},
						{
							value: 'shipped',
							label: 'Expediée',
						}
					]}
				/>
			</div>

			<h1 className="text-lg font-bold mb-2 text-center">Commande {order.reference}</h1>

			<div className="p-2 grid gap-2">
				<div className="grid grid-cols-2 gap-2 mb-2">
					<div className="p-2 border border-black rounded-md grid grid-cols-1 bg-[#FAFAFA]">
						<small className="uppercase">Commandé à:</small>
						<strong>{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
						<small className="uppercase">Le {new Date(order.created_at).toLocaleDateString('fr-FR')}</small>
					</div>
					<div className="p-2 border border-black rounded-md grid grid-cols-1 bg-[#FAFAFA]">
						<small className="uppercase">Livraison prévue à:</small>
						<strong>08:00</strong>
						<small className="uppercase">Le {getDeliveryDate()}</small>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-2">
					<div className="row-span-2 p-2 border border-black rounded-md grid grid-cols-1 gap-2 bg-[#FAFAFA]">
						<h3 className="text-xs font-bold uppercase">Détails de la commande</h3>

						<div>
							<div className="pl-4 mb-4">
								{order.selected_menus?.map((menu, index) => (
									<div key={index} className="flex justify-between gap-2">
										<div className="flex gap-12 text-primary">
											<span>{menu.quantity}x</span>
											<span>
												{menu.name} <small>({menu.vat_id?.code})</small>
											</span>
										</div>

										<span>{Math.round(menu.price).toFixed(2) * menu.quantity}€</span>
									</div>
								))}
							</div>

							<div className="pl-12 mb-12">
								{order.categories?.some(category => category.products.length > 0) && <h3 className="text-xs underline">Produits dans le menu:</h3>}
								{order.categories
									?.filter((category) => category?.products?.length > 0)
									?.map((category, categoryIndex) => (
										<div key={`${category?.id} - ${categoryIndex}`}>
											{category?.products?.length > 0 && (
												<div className="mb-4">
													<h4 className="text-sm">{category.name}</h4>
													{category?.products?.map((product, productIndex) => (
														<div key={`${category?.id} - ${product.id || productIndex}`} className="grid grid-cols-1">
															<div className="text-sm pl-4 flex gap-2">
																<span>-</span>
																<span>{product.quantity}x</span>
																<span>{product.name}</span>
																<span>({product.weight || 0}g)</span> {/* Afficher le poids unitaire */}
																<span>Total: {product.weight * product.quantity}g</span> {/* Afficher le poids total */}
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									))}

								{order.categories?.some(category => category.extra_products.length > 0) && <h3 className="text-xs underline">Hors menu:</h3>}
								{order.categories
									?.filter((category) => category?.extra_products?.length > 0)
									?.map((category, categoryIndex) => (
										<div key={`${category?.id} - ${categoryIndex}`}>
											{category?.extra_products?.length > 0 && (
												<div>
													{category?.extra_products?.map((product, productIndex) => (
														<div key={`${category?.id} - ${product.id || productIndex}`} className="grid grid-cols-1">
															<div className="text-sm flex justify-between">
																<div className="flex gap-2">
																	<span>+</span>
																	<span>{product.quantity}x</span>
																	<span>{product.name}</span>
																	<span>({product.weight || 0}g)</span> {/* Afficher le poids unitaire */}
																	<span>Total: {product.weight * product.quantity}g</span>{' '}
																	{/* Afficher le poids total */}
																</div>
																<span>
																	{Math.round(product.price).toFixed(2) * Math.round(product.quantity).toFixed(2)}€
																</span>
															</div>
														</div>
													))}
												</div>
											)}
										</div>
									))}
							</div>

							<div className="px-8">
								<div className="flex justify-between mb-4">
									<h3 className="text-md font-bold underline">Total articles:</h3>
									<span className="text-md font-bold">{parseFloat(order.total_price - realDeliveryFees).toFixed(2)}€</span>
								</div>

								{order.promo_code && (
									<>
										<div className="flex justify-between mb-2">
											<h3 className="text-md font-bold underline">Code promo / parrainage utilisé:</h3>
											<span className="text-md font-bold">{order.promo_code}</span>
										</div>
									</>
								)}

								<div className="flex justify-between">
									<h3 className="text-md font-bold underline">Total {(order.wallet_discount_amount || order.total_price_with_promo) ? 'avant réduction' : 'TTC'} :</h3>
									<span className="text-md font-bold">
										{((parseFloat(order.total_price)).toFixed(2))}€
									</span>
								</div>

								<div className="flex justify-between mb-2">
									<h3 className="text-md font-bold underline">Dont frais de livraison:</h3>
									<span className="text-md font-bold">
										{(order.is_free_order || (order.promo_code && order.total_price_with_promo === 0))
											? `${realDeliveryFees.toFixed(2)}€`
											: `${parseFloat(order.delivery?.fees || 0).toFixed(2)}€`}
									</span>
								</div>

								{(order.wallet_discount_amount || order.total_price_with_promo) && !order.referral_code ? (
									<>
										<div className="flex justify-between mb-2">
											<h3 className="text-md font-bold underline">Réduction:</h3>
											<span className="text-md font-bold text-green-600">-{parseFloat(order.wallet_discount_amount || (parseFloat(order.total_price) - parseFloat(order.total_price_with_promo))).toFixed(2)}€</span>
										</div>

										<div className="flex justify-between mb-2">
											<h3 className="text-md font-bold underline">Total après réduction:</h3>
											<span className="text-md font-bold text-green-600">{order.wallet_discount_amount || order.total_price_with_promo ? (parseFloat(order.total_price) - parseFloat(order.wallet_discount_amount || (parseFloat(order.total_price) - parseFloat(order.total_price_with_promo)))).toFixed(2) : (parseFloat(order.total_price) + realDeliveryFees).toFixed(2)}€</span>
										</div>
									</>
								) : null}
							</div>
						</div>
					</div>

					<div className="row-span-2 grid grid-cols-1 place-content-center gap-4">
						<div className="p-2 border border-black rounded-md bg-[#FAFAFA]">
							<h3 className="text-xs uppercase font-bold mb-4">Information client:</h3>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Prénom et nom:</p>
								<p className="font-bold">
									{order.delivery?.customer?.firstname} {order.delivery?.customer?.lastname}
								</p>
							</div>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Numéro de téléphone:</p>
								<p className="font-bold">{order.delivery?.customer?.phone}</p>
							</div>

							{order.billing?.customer?.referral_code && (
								<div className="uppercase px-2 text-xs mb-2">
									<p>Code parrainage:</p>
									<p className="font-bold">{order.billing?.customer?.referral_code}</p>
								</div>
							)}
						</div>

						<div className="p-2 border border-black rounded-md bg-[#FAFAFA]">
							<h3 className="text-xs uppercase font-bold mb-4">Information livraison:</h3>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Adresse:</p>
								<p className="font-bold">{order.delivery?.address.address}</p>
							</div>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Email:</p>
								<p className="font-bold">{order.billing?.customer?.email}</p>
							</div>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Entreprise:</p>
								<p className="font-bold">{order.billing?.customer?.company}</p>
								{!order.billing?.customer?.company && <p className="text-[10px] italic">Non renseignée</p>}
							</div>

							<div className="uppercase px-2 text-xs mb-2">
								<p>Commentaire:</p>
								<p className="font-bold">{order.delivery?.customer?.message}</p>
								{!order.delivery?.customer?.message && <p className="text-[10px] italic">Non renseigné</p>}
							</div>
						</div>
					</div>
				</div>

				<div className="p-2 border border-black rounded-md bg-[#FAFAFA] w-1/2 ml-0 grid grid-cols-2">
					<h3 className="text-md uppercase font-bold mb-4">Dont TVA ({getVATCodes()}):</h3>

					<p className="font-bold">{totalVAT}€</p>
				</div>

				{/* Afficher le poids total formaté */}
				<div className="p-2 border border-black rounded-md bg-[#FAFAFA] w-1/2 ml-0 grid grid-cols-2">
					<h3 className="text-md uppercase font-bold mb-4">Poids Total:</h3>
					<p className="font-bold">{formatWeight(totalWeight)}</p>
				</div>

			</div>
		</BOLayout>
	);
};

export default BOOrderDetails;
